<template>
  <div>
    <b-row
      v-for="(condition, conditionIndex) in field.conditions"
      :key="`${field.fieldForm.field}-${conditionIndex}`"
      class="condition-row"
    >
      <b-col md="10">
        <b-form-group>
          <b-input-group>
            <b-row class="w-100">
              <b-col :cols="colSize">
                <b-dropdown
                  :text="convertOperatorToReadable(condition.operator)"
                  class="condition-dropdown dropdown-competition"
                >
                  <b-dropdown-item
                    v-for="(operator, dropdownItemIndex) in field.fieldForm.allowedOperators"
                    @click="onValueChanged(operator.action, 'operator', conditionIndex)"
                    :key="`${field.fieldForm.field}-${dropdownItemIndex}`"
                    >{{ convertOperatorToReadable(operator.action) }}
                  </b-dropdown-item>
                </b-dropdown>
              </b-col>
              <b-col cols="6">
                <b-form-input
                  :id="`${field.fieldForm._id}-value-input-${conditionIndex}`"
                  :min="field.fieldForm.type.min"
                  :max="field.fieldForm.type.max"
                  :value="condition.value"
                  :type="inputType"
                  @change="onInputChanged($event, conditionIndex)"
                >
                </b-form-input>
              </b-col>
              <b-col cols="3" v-if="hasLogicOperators">
                <b-dropdown
                  :text="convertLogicOperatorToReadable(condition.logic)"
                  class="condition-dropdown dropdown-competition"
                  :disabled="field.fieldForm.allowedLogicOperators.length === 1"
                >
                  <b-dropdown-item
                    v-for="(logicOperator, dropdownItemIndex) in field.fieldForm.allowedLogicOperators"
                    @click="onValueChanged(logicOperator.action, 'logic', conditionIndex)"
                    :key="`${field.fieldForm.field}-${dropdownItemIndex}`"
                  >
                    {{ convertLogicOperatorToReadable(logicOperator.action) }}
                  </b-dropdown-item>
                </b-dropdown>
              </b-col>
            </b-row>
          </b-input-group>
        </b-form-group>
        <company-field-errors
          v-if="getError(conditionIndex).length > 0"
          :errors="getError(conditionIndex)"
          :innerKey="`condition-${conditionIndex}-error-`"
        />
      </b-col>
      <b-col md="2">
        <span class="delete-text" @click="onDeleteClicked(conditionIndex)">
          <delete-field :hasPadding="true" />
        </span>
      </b-col>
    </b-row>
    <b-row v-if="showAddNew" class="add-filter" @click="onAddFilterClicked" md="12">
      <add-new-field />
    </b-row>
  </div>
</template>

<script>
import { getReadableOperator } from '../../models/operator'
import { getReadableLogicOperator, LOGIC_OPERATOR } from '../../models/logicOperator'
import { COMPETITION_FIELD_TYPE } from '../../models/competitionFieldType'
import AddNewField from './AddNewField.vue'
import CompanyFieldErrors from './CompanyFieldErrors.vue'
import DeleteField from './DeleteField.vue'
export default {
  name: 'ArrayField',
  props: ['field', 'onChanged', 'getFieldFormErrors', 'onDelete'],
  components: { CompanyFieldErrors, AddNewField, DeleteField },
  computed: {
    colSize: function() {
      if (this.hasLogicOperators) {
        return '3'
      }
      return '6'
    },
    showAddNew: function() {
      const fieldOperatorLength = this.field.fieldForm.allowedOperators.length
      const conditionLength = this.field.conditions.length
      if (this.field.fieldForm.allowedLogicOperators === undefined) {
        return fieldOperatorLength !== conditionLength
      } else {
        const fieldLogicOperatorLength = this.field.fieldForm.allowedLogicOperators.length
        return fieldOperatorLength * fieldLogicOperatorLength !== conditionLength
      }
    },
    inputType: function() {
      switch (this.field.fieldForm.type.itemType) {
        case COMPETITION_FIELD_TYPE.NUMBER:
          return 'number'
        case COMPETITION_FIELD_TYPE.STRING:
          return 'text'
        case COMPETITION_FIELD_TYPE.BOOLEAN:
          return 'bool'
        default:
          return ''
      }
    },
    hasLogicOperators: function() {
      return this.field.fieldForm.allowedLogicOperators !== undefined
    },
    conditions: function() {
      return this.field.conditions
    },
    operators: function() {
      const result = this.field.allowedOperators.map(allowedOperator => ({
        value: allowedOperator.action,
        name: getReadableOperator(allowedOperator.action)
      }))
      return result
    }
  },
  methods: {
    getError: function(conditionIndex) {
      return this.getFieldFormErrors({ key: this.field.fieldForm.field, index: conditionIndex })
    },
    onFieldChanged: function(updatedField, conditionIndex) {
      this.onChanged(updatedField, conditionIndex)
    },
    onInputChanged: function(value, index) {
      const input = document.getElementById(`${this.field.fieldForm._id}-value-input-${index}`)
      const regex = new RegExp(this.field.fieldForm.type.itemRegex)
      if (regex.test(value) || value.length === 0) {
        this.onValueChanged(value, 'value', index)
      } else {
        input.value = this.field.conditions[index].value
      }
    },
    onValueChanged: function(value, key, index) {
      const updatedField = { ...this.field }
      updatedField.conditions = [...updatedField.conditions]
      updatedField.conditions[index] = { ...updatedField.conditions[index] }
      updatedField.conditions[index][key] = value
      this.onFieldChanged(updatedField, index)
    },
    onAddFilterClicked: function() {
      const updatedField = { ...this.field }
      updatedField.conditions = [...updatedField.conditions]
      if (this.hasLogicOperators) {
        updatedField.conditions.push({
          operator: this.field.fieldForm.allowedOperators[0].action,
          value: '',
          logic: this.field.fieldForm.allowedLogicOperators[0].action
        })
      } else {
        updatedField.conditions.push({
          operator: this.field.fieldForm.allowedOperators[0].action,
          logic: { action: LOGIC_OPERATOR.AND },
          value: ''
        })
      }
      this.onFieldChanged(updatedField)
    },
    convertOperatorToReadable: function(operator) {
      const friendlyNameOfAction = this.field.fieldForm.allowedOperators.filter(op => op.action === operator)[0]
        .friendlyNameOfAction
      if (friendlyNameOfAction !== undefined) {
        return friendlyNameOfAction
      } else {
        return getReadableOperator(operator)
      }
    },
    convertLogicOperatorToReadable: function(logic) {
      const friendlyNameOfAction = this.field.fieldForm.allowedLogicOperators.filter(op => op.action === logic)[0]
        .friendlyNameOfAction
      if (friendlyNameOfAction !== undefined) {
        return friendlyNameOfAction
      } else {
        return getReadableLogicOperator(logic)
      }
    },
    onDeleteClicked: function(index) {
      const updatedField = { ...this.field }
      updatedField.conditions = [...updatedField.conditions]
      updatedField.conditions.splice(index, 1)
      this.onFieldChanged(updatedField, index)
    }
  }
}
</script>

<style></style>
